<template>
  <div class="details hoteldetail">
    <div class="details__title">
      <div class="details__photos" v-if="hotelImages.length">
        <vue-easy-lightbox
          scrollDisabled
          escDisabled
          moveDisabled
          :visible="lightBoxVisible"
          :imgs="lightboxImgs"
          :index="lightboxIndex"
          @hide="lightBoxVisible = false"
        ></vue-easy-lightbox>
        <div class="details__photos">
          <div
            v-for="(picture, $index) in hotelImages.slice(0, 3)"
            :key="$index"
            v-bind:style="{ 'background-image': `url(${picture})` }"
            @click="lightBoxVisible = true"
            :class="`details__photos__photo-box-${$index + 1}`"
          ></div>
          <link
            v-for="(picture, $index) in hotelImages.slice(3)"
            :key="$index"
            rel="prefetch"
            :href="picture"
          />
        </div>
      </div>
      <div
        class="wrapper container mt-0 is-flex is-flex-direction-column is-justify-content-space-between"
        v-if="details"
      >
        <span>
          <button
            @click.stop="gotoResults()"
            class="c-button c-button--secondary c-button-small"
          >
            {{
              locationPath === "direct" ? $t("backToHome") : $t("backToSearch")
            }}
          </button>
        </span>
      </div>
    </div>
    <div
      class="details__content container loading-container loading-container--full-height"
      v-if="!isLoaded"
    >
      <h3 class="title is-3">{{ $t("hotelLoading") }}</h3>
      <br />
      <img width="50" height="60" src="@/assets/images/loader.svg" />
    </div>

    <div
      class="details__content container"
      v-if="isLoaded && details"
      style="position: relative; top: -130px"
    >
      <div class="columns mb-3 m-0">
        <div
          class="c-button c-button--secondary mt-4"
          @click="lightBoxVisible = true"
        >
          {{ $t("viewAllPhotosText", { count: hotelImages.length }) }}
        </div>
      </div>
      <div class="columns is-align-items-flex-start is-multiline m-0">
        <div
          class="column is-9-desktop is-full-mobile mobile-u-width-100 mobile-p-0"
        >
          <div
            class="columns details__content__box mobile-u-width-100 mobile-m-0"
          >
            <div class="column is-8 p-0 pr-4">
              <div class="is-flex is-flex-direction-column">
                <span class="details__content__hotel-name" style="">
                  <div>
                    <span
                      class="u-display--block is-size-6"
                      v-if="details.isRecommended"
                    >
                      <span
                        class="u-text-color-tertiary u-display--flex u-weight--bold is-align-items-center"
                      >
                        <img
                          src="@/assets/images/icons/price-tag.svg"
                          class="mr-1"
                          width="20"
                        />
                        {{ $t("recommended.hotel") }}
                      </span>
                    </span>
                    <h1 class="mt-0">
                      {{ details?.name }}
                    </h1>
                    <img
                      class="js-tooltip"
                      v-if="details.hasGreenKey"
                      style="margin-top: 3px; margin-left: 6px"
                      width="35"
                      height="50"
                      src="@/assets/images/greenkey-nobg.png"
                      :data-tooltip-content="$t('hasGreenKey')"
                      data-tooltip-placement="bottom"
                    />
                  </div>
                </span>
                <ReviewsSummary
                  :score="getRating()"
                  :amount="getRatingCount()"
                />
              </div>

              <!-- hotel amenities -->
              <div
                v-if="highlightedAmenities.length"
                class="c-amenity-badges mt-4"
              >
                <div
                  v-for="amenity in highlightedAmenities"
                  :key="amenity.name"
                  class="c-amenity-badges__item"
                >
                  <i
                    :class="[amenity.icon, 'c-amenity-badges__icon']"
                    aria-hidden="true"
                  ></i>
                  <span>{{ $t("expedia." + amenity.name) }}</span>
                </div>
              </div>

              <!-- checkin & out -->
              <div v-if="getPolicies() != null">
                <h5
                  class="title is-5 mt-5 mb-3"
                  v-html="$t('inOutCheckTimes')"
                ></h5>
                <br />
                <p
                  v-if="getPolicies().checkInPolicy"
                  v-safe-html="getPolicies().checkInPolicy.join('<br />')"
                ></p>
                <p v-if="has24hFrontDesk && getPolicies().checkInStartTime">
                  {{ $t("checkInFrom") }} {{ getPolicies().checkInStartTime }}
                  {{ $t("checkIn24h") }}
                </p>
                <p v-else-if="has24hFrontDesk">
                  {{ $t("checkInFrom") }} {{ $t("checkIn24h") }}
                </p>
                <p v-else-if="getPolicies().checkInStartTime">
                  {{ $t("checkInFrom") }} {{ getPolicies().checkInStartTime }}
                  <template v-if="getPolicies().checkInEndTime">
                    – {{ getPolicies().checkInEndTime }}
                  </template>
                </p>
                <p class="" v-if="getPolicies().checkOutTime">
                  {{ $t("checkOutBefore") }} {{ getPolicies().checkOutTime }}
                </p>
                <p
                  class="mt-1"
                  v-if="getPolicies().checkOutPolicy"
                  v-safe-html="getPolicies().checkOutPolicy.join('<br />')"
                ></p>
                <div class="mt-1" v-if="getPolicies().checkInInstructions">
                  <a
                    href="#"
                    class="u-text-decoration--underline"
                    @click.prevent="
                      showCheckInInstructions = !showCheckInInstructions
                    "
                  >
                    {{ $t("showCheckInInstructions") }}
                  </a>
                </div>
              </div>
            </div>
            <div class="column is-4 p-0 mobile-mt-5">
              <div class="details__content__address m-x">
                <div
                  id="details-map"
                  ref="detailsMap"
                  class="details__map mt-3 mb-5"
                ></div>
                <h5 class="title is-5 mb-3" v-html="$t('address')"></h5>
                <div>{{ details.address }} {{ details.city }}</div>
                <a
                  class="maps-trigger"
                  target="_blank"
                  :href="getGoogleMapsLink()"
                >
                  <svg
                    class="mr-1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 384 512"
                  >
                    <path
                      d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"
                    />
                  </svg>
                  {{ $t("showOnGoogleMaps") }}</a
                >
                <div v-if="details?.content?.locationAttribute">
                  <br class="is-hidden-touch" />
                  {{
                    details?.content?.locationAttribute.distanceFromCityCenter
                      .distance
                  }}
                  {{
                    details?.content?.locationAttribute.distanceFromCityCenter
                      .unit
                  }}
                  {{ $t("distanceFromCityAbs") }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="column is-3-desktop is-full details__content__box mobile-mt-5 mobile-m-0"
          id="change-data"
        >
          <div class="content mb-0">
            <form @submit.prevent="submitSearch">
              <h5 data-v-836c7588="" class="title is-5 mb-5">
                Geselecteerde data
              </h5>
              <div
                class="main-searchbar__checkin-input searchbar__input-wrapper mr-4 mb-0"
              >
                <DatepickerFoldout
                  :checkin="checkin"
                  :nights="nights"
                  @selectDates="selectDates"
                />
              </div>
              <div
                class="main-searchbar__meta-input searchbar__input-wrapper mr-4"
              >
                <div class="columns is-mobile">
                  <div class="column">
                    <div class="searchbar__icon">
                      <i
                        class="fas fa-person"
                        :title="$t('searchFilters.numberOfPersons')"
                        aria-hidden="true"
                      ></i>
                    </div>
                    <input
                      id="adults"
                      type="number"
                      class="searchbar__input"
                      min="1"
                      max="8"
                      :placeholder="$t('searchFilters.numberOfRooms')"
                      v-model="editableRooms"
                    />
                  </div>
                </div>
              </div>
              <button
                type="submit"
                class="c-button c-button--secondary mt-4 c-button--fullwidth"
              >
                {{ $t("findRooms") }}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div
        v-if="availableRooms.length > 0"
        class="mt-5 mb-5 details__content__box"
        id="rooms"
      >
        <h5 class="title is-5 mb-5">{{ $t("selectYourRoom") }}</h5>
        <div
          class="details__content container loading-container"
          v-if="isRefreshingDetails"
        >
          <h3 class="title is-3">{{ $t("hotelLoading") }}</h3>
          <br />
          <img width="50" height="60" src="@/assets/images/loader.svg" />
        </div>
        <div v-else>
          <div class="mb-5">
            {{
              $t("basedOn", {
                count: parseInt(nights),
                adults: parseInt(adults),
                rooms: parseInt(localRooms),
                startDate: startDate,
                endDate: endDate
              })
            }}<br />
            {{ $t("basedOnChange") }}
            <a
              href="#change-data"
              class="u-text-decoration--underline"
              @click.prevent="scrollToElement('change-data')"
              >{{ $t("basedOnChangeLink") }}</a
            >
            {{ $t("basedOnChangeSuffix") }}
          </div>
          <div class="columns is-multiline">
            <div
              v-for="room in availableRooms"
              :key="room.roomId"
              class="column is-3-fullhd is-4-desktop is-6-tablet is-full-mobile"
            >
              <RoomCard
                :room="room"
                :hotel="details"
                :nights="nights"
                :is-bookable="isRoomBookable(room)"
                @book="gotoReservationFormForRoom"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="columns is-align-items-flex-start is-multiline m-0 mb-5 mobile-p-0"
      >
        <div
          class="column pl-0 pt-0 pb-0 mobile-u-width-100"
          :class="details.hasGreenKey ? 'is-half-desktop' : 'is-full pr-0'"
        >
          <div class="details__content__box">
            <h5 class="title is-5 mb-5">{{ $t("tarieven") }}</h5>
            <p v-if="getDetailFeesHtml()" v-safe-html="getDetailFeesHtml()"></p>
          </div>
        </div>
        <div
          class="is-half-desktop column pr-0 pt-0 pb-0 mobile-u-width-100 mobile-mt-5 mobile-p-0"
        >
          <div class="details__content__box content" v-if="details.hasGreenKey">
            <h5 class="title is-5 mb-5">
              <img
                width="40"
                height="50"
                src="@/assets/images/greenkey-nobg.png"
                class="mr-2"
              />
              <span v-html="$t('hasGreenKeyTitle')"></span>
            </h5>
            <p>
              {{ $t("hasGreenKeyExplanation") }}
            </p>
            <a
              class="u-text-decoration--underline"
              href="https://www.greenkey.nl/"
              target="_blank"
              >{{ $t("moreInfo") }}</a
            >
          </div>
        </div>
      </div>

      <div class="details__content--container m-0">
        <div
          v-if="
            details?.content ||
            details?.policies ||
            (details?.amenities && details.amenities.length)
          "
        >
          <div
            class="details__content__box content"
            v-if="
              getAmenities() || (details.amenities && details.amenities.length)
            "
          >
            <amenities-list
              v-if="getAmenities()"
              :amenities-list="getAmenities().propertyAmenities"
              :title="$t('hotelAmenities')"
            />
            <amenities-list
              v-else
              :flat-amenities-list="details.amenities"
              :title="$t('hotelAmenities')"
            />
          </div>

          <div
            class="details__content__box content"
            v-if="combinedSpecialInstructions.length || details?.country"
          >
            <ContentList
              :title="'expedia.goodToKnow'"
              :content-list="combinedSpecialInstructions"
              v-if="combinedSpecialInstructions.length"
              :plainText="true"
            />

            <VisaMessage :country="details?.country" />

            <VaccinationAdvice :country="details?.country" />
          </div>
          <div
            class="details__content__box content"
            v-if="getPolicies() && getPolicies().petPolicy"
          >
            <ContentList
              :title="'expedia.petPolicy'"
              :content-list="getPolicies().petPolicy"
              v-if="getPolicies().petPolicy"
            />
          </div>
        </div>
        <div v-else>
          <p>{{ $t("noHotelContent") }}</p>
        </div>
      </div>
    </div>
    <BaseModal
      :show="showCheckInInstructions"
      title="Check-in"
      @close="showCheckInInstructions = false"
    >
      <div v-safe-html="getPolicies().checkInInstructions"></div>
    </BaseModal>
    <BaseModal
      :show="showRoomOptionsDefaults"
      :title="$t('roomOptionsDefaultsPopup.title')"
      @close="dismissRoomOptionsDefaults"
    >
      <div v-safe-html="$t('roomOptionsDefaultsPopup.description')"></div>
      <template #footer>
        <button
          type="button"
          class="c-button c-button--primary"
          @click="dismissRoomOptionsDefaults"
        >
          {{ $t("done") }}
        </button>
      </template>
    </BaseModal>
    <NotificationMessage
      :level="notification.level"
      :autohide="false"
      :title="notification.title"
      :message="notification.message"
      :show="notification.show"
      @toggle="toggleNotification"
    />
  </div>
</template>

<script>
// eslint-disable no-undef
import BaseModal from "@/components/shared/BaseModal.vue";
import mapboxgl from "!mapbox-gl";
import introJs from "intro.js";
import VueEasyLightbox from "vue-easy-lightbox";
import CommonHelper from "@/helpers/common.helper.js";
import HotelHelper from "@/helpers/hotel.helper.js";
import FilterHelper from "@/helpers/filters.helper.js";
import DatepickerFoldout from "./components/landingpage/DatepickerFoldout.vue";
import ReviewsSummary from "./components/hoteldetail/ReviewsSummary.vue";
import AmenitiesList from "./components/hoteldetail/AmenitiesList.vue";
import MapsHelper from "@/helpers/maps.helper.js";
import { isOpenGdsOffer } from "@/helpers/offer.helper.js";
import { buildAvailableRooms } from "@/helpers/rooms.helper.js";
import ContentList from "./components/hoteldetail/ContentList.vue";
import RoomCard from "./components/hoteldetail/RoomCard.vue";
import VaccinationAdvice from "@/components/VaccinationAdvice.vue";
import VisaMessage from "@/components/VisaMessage.vue";

export default {
  components: {
    VueEasyLightbox,
    ContentList,
    ReviewsSummary,
    AmenitiesList,
    VaccinationAdvice,
    VisaMessage,
    DatepickerFoldout,
    BaseModal,
    RoomCard
  },
  props: {
    locationPath: {
      type: String,
      default: ""
    },
    hotelId: {
      type: String,
      default: ""
    },
    refundable: {
      type: String,
      default: "true"
    },
    greenKey: {
      type: String,
      default: "true"
    },
    checkin: {
      type: String,
      default: null
    },
    adults: {
      type: String,
      default: "1"
    },
    rooms: {
      type: String,
      default: "1"
    },
    feedbackId: {
      type: String,
      default: null
    },
    nights: {
      type: String,
      default: null
    },
    breakfastIncluded: {
      type: String,
      default: "false"
    }
  },
  data() {
    return {
      isLoaded: false,
      fetchedFeedbackId: null,
      details: null,
      lightBoxVisible: false,
      lightboxIndex: 0,
      map: null,
      localRooms: this.rooms || "1",
      selectedRoomId: null,
      showCheckInInstructions: false,
      showRoomOptionsDefaults: false,
      dates: {
        Checkin: this.checkin || null,
        Nights: this.nights || null
      },
      isRefreshingDetails: false,
      companyInfo: null
    };
  },
  name: "HotelDetail",
  mounted() {
    mapboxgl.accessToken = this.MAPBOX_ACCESS_TOKEN;
    window.scrollTo(0, 0);

    const roomTypePopupCookie =
      "expediaRoomTypesUpdate-2026-06-dontShowAgain=true";
    const hasRoomTypePopupCookie = document.cookie
      .split(";")
      .map((entry) => entry.trim())
      .includes(roomTypePopupCookie);

    if (!hasRoomTypePopupCookie) {
      const roomTypesIntro = introJs();
      const markRoomTypesSeen = () => {
        document.cookie = `${roomTypePopupCookie}; path=/; max-age=31536000; Secure; SameSite=Lax`;
      };

      roomTypesIntro.setOptions({
        nextLabel: this.$t("next"),
        skipLabel: this.$t("skip"),
        prevLabel: this.$t("back"),
        doneLabel: this.$t("done"),
        steps: [
          {
            intro: `<b>${this.$t("expediaRoomTypesUpdatePopup.title")}</b><br><br>${this.$t("expediaRoomTypesUpdatePopup.description")}`
          }
        ]
      });

      roomTypesIntro.oncomplete(markRoomTypesSeen);
      roomTypesIntro.onexit(markRoomTypesSeen);
      roomTypesIntro.onskip(markRoomTypesSeen);
      roomTypesIntro.start();
    }

    const roomOptionsDefaultsCookie =
      "roomOptionsDefaults-2026-08-dontShowAgain=true";
    const hasRoomOptionsDefaultsCookie = document.cookie
      .split(";")
      .map((entry) => entry.trim())
      .includes(roomOptionsDefaultsCookie);

    if (!hasRoomOptionsDefaultsCookie) {
      this.showRoomOptionsDefaults = true;
    }
  },
  created() {
    this.loadDetails();
    this.loadCompanyInfo();
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove();
    }
    document.removeEventListener("wheel", this.preventWheelZoom, {
      capture: true
    });
  },

  methods: {
    dismissRoomOptionsDefaults() {
      document.cookie =
        "roomOptionsDefaults-2026-08-dontShowAgain=true; path=/; max-age=31536000; Secure; SameSite=Lax";
      this.showRoomOptionsDefaults = false;
    },
    getAmenities() {
      return this.details?.content?.amenities
        ? this.details.content.amenities[this.languageIso.toLowerCase()]
        : null;
    },
    getPolicies() {
      return this.details.policies || null;
    },
    getDetailFeesHtml() {
      const policies = this.getPolicies();
      const feeParts = [
        ...(policies?.paymentPolicy?.propertyFees || []),
        ...(policies?.paymentPolicy?.optionalExtras || [])
      ].filter((fee) => typeof fee === "string" && fee.trim().length > 0);

      return feeParts.length > 0 ? feeParts.join("<br />") : null;
    },
    getRating() {
      return this.details.reviewScore
        ? parseFloat(this.details.reviewScore).toPrecision(2)
        : "0";
    },
    getRatingCount() {
      return parseInt(this.details.reviewAmount ?? 0);
    },
    getGoogleMapsLink() {
      const query = encodeURIComponent(
        `${this.details.name}, ${this.details.address}, ${this.details.city}`
      );
      return `https://www.google.com/maps/search/?api=1&query=${query}`;
    },
    async gotoReservationFormForRoom(room, offer, preferences) {
      this.selectedRoomId = room.roomId;
      this.track("gotoHotelReservationHotelDetail", {
        locationPath: this.locationPath,
        hotelId: this.details.hotelId,
        checkin: this.checkin,
        nights: this.nights,
        greenKey: this.greenKey,
        adults: this.adults || 1,
        rooms: this.localRooms || 1,
        refundable: this.refundable || true,
        feedbackId: this.feedbackId,
        breakfastIncluded: this.breakfastIncluded
      });

      const companyInfo = this.companyInfo;
      const wantsBreakfastSpecialRequest =
        !!preferences?.wantsBreakfastSpecialRequest;
      const offerData = {
        offer: offer ? JSON.parse(JSON.stringify(offer)) : null,
        companyCode: companyInfo?.companyCode || null,
        feedbackIdGuid: this.feedbackId || null,
        checkin: this.dates.Checkin || this.checkin,
        nights: parseInt(this.dates.Nights || this.nights) || 1,
        adults: parseInt(this.adults) || 1,
        rooms: parseInt(this.localRooms) || 1,
        hasAgreedPrice: !!room.hasAgreedPrice,
        hasOpenGdsOffers: !!room.offers?.some(isOpenGdsOffer),
        requestReservation:
          !!room.requestReservation || wantsBreakfastSpecialRequest,
        breakfastSpecialRequest: wantsBreakfastSpecialRequest
      };

      // When the reservation goes through OpenGds (CTA "reservering
      // aanvragen"), ensure the offer carries reservationType "OpenGds"
      // so the backend processes it correctly.
      // Breakfast special requests are routed through BookingPortal;
      if (offerData.requestReservation && offerData.offer) {
        offerData.offer.reservationType = wantsBreakfastSpecialRequest
          ? "BookingPortal"
          : "OpenGds";
      }

      this.$router.push({
        name: this.PAGES.HotelReservationForm,
        params: {
          locationPath: this.locationPath || "direct",
          hotelId: this.details.hotelId,
          checkin:
            this.dates.Checkin ||
            offerData.checkin ||
            new Date(Date.now() + 86400000).toISOString().slice(0, 10),
          nights: this.dates.Nights || offerData.nights || 1,
          adults: this.adults || offerData.adults || 1,
          rooms: this.localRooms || offerData.rooms || 1,
          refundable:
            offer?.refundable ?? offer?.isRefundable ?? this.refundable ?? true,
          greenKey: this.greenKey,
          breakfastIncluded:
            offer?.breakfastIncluded ??
            offer?.isBreakfastIncluded ??
            this.breakfastIncluded ??
            false
        },
        state: { offerData }
      });
    },
    gotoResults() {
      if (this.locationPath === "direct") {
        this.$router.push({
          name: this.PAGES.LandingPage
        });
      } else {
        this.$router.push({
          name: this.PAGES.SearchResult,
          params: {
            locationPath: this.locationPath,
            checkin: this.dates.Checkin || this.checkin,
            nights: this.dates.Nights || this.nights,
            greenKey: this.greenKey,
            refundable: this.refundable || true,
            adults: this.adults || 1,
            rooms: this.localRooms || 1,
            feedbackId: this.feedbackId
          }
        });
      }
    },
    preventWheelZoom(e) {
      const lightbox = document.querySelector(".vel-modal");
      if (lightbox && lightbox.contains(e.target)) {
        e.stopPropagation();
      }
    },
    isRoomBookable(room) {
      if (this.canBookOnBehalfOfOthers) return true;
      const lowestPrice = (r) =>
        Math.min(...r.offers.map((o) => o.nightlyTotal));
      // If any room has a price agreement, only rooms at the cheapest agreed price are bookable
      const agreedRooms = this.availableRooms.filter(
        (r) =>
          (r.isOpenGdsMatch || r.hasAgreedPrice) &&
          r.offers?.some((o) => o.nightlyTotal > 0)
      );
      if (agreedRooms.length) {
        const cheapestPrice = Math.min(...agreedRooms.map(lowestPrice));
        return (
          (room.isOpenGdsMatch || room.hasAgreedPrice) &&
          room.offers?.some((o) => o.nightlyTotal > 0) &&
          lowestPrice(room) === cheapestPrice
        );
      }
      // No price agreement: only rooms at the cheapest price are bookable
      const roomsWithOffers = this.availableRooms.filter((r) =>
        r.offers?.some((o) => o.nightlyTotal > 0)
      );
      if (!roomsWithOffers.length) return true;
      const cheapestPrice = Math.min(...roomsWithOffers.map(lowestPrice));
      return (
        room.offers?.some((o) => o.nightlyTotal > 0) &&
        lowestPrice(room) === cheapestPrice
      );
    },
    async loadCompanyInfo() {
      const companyInfo = await CommonHelper.getCompanyInfo();
      this.companyInfo = companyInfo;
    },
    async loadDetails() {
      // eslint-disable-next-line no-undef
      const url = `${this.AVAILABILITY_API_URL}/hotel/gethotelwithprices/${this.hotelId}`;
      const postData = {
        rooms: this.localRooms || 1,
        adults: this.adults || 1,
        refundable: this.refundable === "true",
        feedbackId:
          this.fetchedFeedbackId != -1
            ? this.fetchedFeedbackId || this.feedbackId || ""
            : ""
      };

      if (this.checkin && this.nights) {
        postData.checkin = this.checkin;
        postData.nights = this.nights;
      }

      const { status, data } = await this.axios.post(url, postData);
      if (status < 400) {
        if (data) {
          if (data.feedbackId) {
            this.fetchedFeedbackId = data.feedbackId;
          }
          if (data.progress < 90) {
            setTimeout(() => this.loadDetails(), 600);
          } else {
            this.details = { ...data };
            this.isLoaded = true;
            this.isRefreshingDetails = false;
            if (
              this.availableRooms.length > 0 &&
              !this.availableRooms.some(
                (room) => room.roomId === this.selectedRoomId
              )
            ) {
              this.selectedRoomId = this.availableRooms[0].roomId;
            }
            await this.$nextTick();
            this.loadMap(data);
          }
        } else {
          this.fetchedFeedbackId = -1;
          this.loadDetails();
        }
      } else {
        this.notification.title = "Er ging iets mis";
        this.notification.message =
          "Er gaat iets mis op de server. Probeer het later opnieuw.";
        this.notification.level = "danger";
        this.notification.show = true;
      }
    },
    loadMap(location) {
      const mapContainer = this.$refs.detailsMap;
      if (!mapContainer) return;
      const myCenter = [location.longitude, location.latitude];
      this.map = MapsHelper.getNewMap(mapContainer, true, {
        cooperativeGestures: true
      });
      this.map.setCenter(myCenter).setZoom(11);
      new mapboxgl.Marker().setLngLat(myCenter).addTo(this.map);
    },
    selectDates(dates) {
      this.dates = dates;
    },
    scrollToElement(id) {
      const el = document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 150;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    },
    submitSearch() {
      const newCheckin = this.dates.Checkin;
      const newNights = String(this.dates.Nights);
      const newRooms = this.localRooms || "1";
      const newAdults = this.adults || "1";

      const paramsChanged =
        newCheckin !== this.checkin ||
        newNights !== String(this.nights) ||
        newRooms !== this.rooms ||
        newAdults !== this.$route.params.adults;

      this.isRefreshingDetails = true;

      if (paramsChanged) {
        this.$router.push({
          name: this.PAGES.HotelDetail,
          params: {
            locationPath: this.locationPath,
            hotelId: this.hotelId,
            checkin: newCheckin,
            nights: newNights,
            refundable: this.refundable || "true",
            greenKey: this.greenKey || "true",
            adults: newAdults,
            rooms: newRooms,
            feedbackId: this.feedbackId,
            breakfastIncluded: this.breakfastIncluded || "false"
          }
        });
      } else {
        this.loadDetails();
      }

      this.$nextTick(() => {
        this.scrollToElement("rooms");
      });
    }
  },
  computed: {
    hotelImages() {
      return Array.isArray(this.details?.photos)
        ? this.details.photos.filter((p) => typeof p === "string" && p)
        : [];
    },
    lightboxImgs() {
      return this.hotelImages.map((src, i) => ({
        src,
        title: `${this.details?.name || ""}  ${i + 1}/${this.hotelImages.length}`
      }));
    },
    isDirect() {
      return this.locationPath === "direct";
    },
    startDate() {
      const dateRange = CommonHelper.getDateRange(this.checkin, this.nights);
      return dateRange?.startDateString || null;
    },
    endDate() {
      const dateRange = CommonHelper.getDateRange(this.checkin, this.nights);
      return dateRange?.endDateString || null;
    },
    highlightedAmenities() {
      return FilterHelper.getAvailablePrimeAmenities(this.details?.amenities);
    },
    has24hFrontDesk() {
      return this.highlightedAmenities.some((a) => a.name === "front_desk_24h");
    },
    combinedSpecialInstructions() {
      const policies = this.details?.policies;
      if (!policies) return [];
      return [
        ...(policies.specialInstructions || []),
        ...(policies.knowBeforeYouGo || [])
      ];
    },
    availableRooms() {
      // Logic lives in src/helpers/rooms.helper.js (heavily commented).
      return buildAvailableRooms(this.details, this.nights);
    },
    canBookOnBehalfOfOthers() {
      return HotelHelper.canBookOnBehalfOfOthers(this.companyInfo);
    },
    hasOffer() {
      return this.availableRooms.some((room) =>
        room.offers?.some((o) => o.nightlyTotal > 0)
      );
    },
    editableRooms: {
      get() {
        return this.localRooms;
      },
      set(value) {
        this.localRooms = value;
      }
    }
  },
  watch: {
    rooms(newValue) {
      this.localRooms = newValue || "1";
    },
    "$route.params": {
      handler(newParams, oldParams) {
        if (
          newParams.checkin !== oldParams.checkin ||
          newParams.nights !== oldParams.nights ||
          newParams.rooms !== oldParams.rooms ||
          newParams.adults !== oldParams.adults
        ) {
          this.fetchedFeedbackId = null;
          this.loadDetails();
        }
      }
    },
    lightBoxVisible(visible) {
      if (visible) {
        document.addEventListener("wheel", this.preventWheelZoom, {
          passive: false,
          capture: true
        });
      } else {
        document.removeEventListener("wheel", this.preventWheelZoom, {
          capture: true
        });
      }
    },
    availableRooms: {
      handler(newRooms) {
        if (newRooms.length > 0) {
          const hasSelectedRoom = newRooms.some(
            (room) => room.roomId === this.selectedRoomId
          );
          if (!hasSelectedRoom) {
            this.selectedRoomId = newRooms[0].roomId;
          }
        } else {
          this.selectedRoomId = null;
        }
      },
      immediate: true
    }
  }
};
</script>
<style lang="scss" scoped>
.loading-container {
  text-align: center;

  &--full-height {
    height: 80vh;
  }
}

:deep(.vel-img) {
  transform: none !important;
}
</style>
